/**
 * PostHog product analytics for the cabinet.
 *
 * The project API key is a PUBLIC token — it's safe to ship in the
 * bundle and identifies the project, not the account. We read it from
 * `VITE_POSTHOG_KEY` at build time; if it's missing or empty (local
 * dev default), analytics is a no-op and no SDK code initializes.
 *
 * `track(event, props)` is the only call site cartridges and the shell
 * should use — keeps the PostHog dependency contained to this module so
 * we can swap providers later without touching every call site.
 */

import { posthog } from 'posthog-js';

let initialized = false;

export function initAnalytics(): void {
  if (initialized) return;

  const key = import.meta.env?.VITE_POSTHOG_KEY?.trim();
  if (!key) {
    // No key configured — analytics disabled. Common for local dev and
    // ephemeral previews. Don't warn; this is the documented default.
    return;
  }

  const host =
    import.meta.env?.VITE_POSTHOG_HOST?.trim() || 'https://us.i.posthog.com';

  posthog.init(key, {
    api_host: host,
    // The cabinet captures its own lifecycle events; we don't need
    // PostHog's autocapture clicking around the canvas-heavy DOM.
    autocapture: false,
    // Session replay records DOM mutations as a video-style playback.
    // For a canvas-heavy PWA the DOM is mostly static chrome and the
    // gameplay isn't visible in the recording anyway — pure ingest cost
    // with no observability payoff. Off at the client level so it stays
    // off even if the project toggle in PostHog flips on.
    disable_session_recording: true,
    capture_pageview: true,
    capture_pageleave: true,
    person_profiles: 'identified_only',
  });

  initialized = true;
}

export function track(event: string, props?: Record<string, unknown>): void {
  if (!initialized) return;
  posthog.capture(event, props);
}

export function identify(
  distinctId: string,
  props?: Record<string, unknown>,
): void {
  if (!initialized) return;
  posthog.identify(distinctId, props);
}

export function resetAnalytics(): void {
  if (!initialized) return;
  posthog.reset();
}
